import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { EditorNameHint, checkEditorName } from './EditorNameCheck';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { TermsCheckbox } from './TermsAccept';
import { User, Building2, Mail, Lock, Phone, MapPin, Calendar, Users, BookOpen, CreditCard, DollarSign, FileText, Globe, AtSign } from 'lucide-react';

const CadField = ({ id, label, icon: Icon, error, type = 'text', ...rest }) => (
  <div className="form-field">
    <Label htmlFor={id}>{label}</Label>
    <div className="input-with-icon">
      <Icon className="input-icon" />
      <Input id={id} name={id} type={type} data-testid={`cadastro-${id}`} className={error ? 'input-error' : ''} {...rest} />
    </div>
    {error && <span className="error-text">{error}</span>}
  </div>
);

const CadastroPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Credenciais
    email: '',
    senha: '',
    // Info Administrador
    adminNome: '',
    adminCpf: '',
    adminDataNascimento: '',
    adminEndereco: '',
    adminTelefone: '',
    // Info Editora
    editoraNome: '',
    editoraRazaoSocial: '',
    editoraEndereco: '',
    editoraEmail: '',
    editoraTelefone: '',
    editoraCnpj: '',
    editoraDataAbertura: '',
    editoraCep: '',
    editoraInscricaoEstadual: '',
    editoraSite: '',
    editoraInstagram: '',
    termsAccepted: false
  });
  const [loading, setLoading] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) newErrors.email = 'E-mail é obrigatório';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'E-mail inválido';
    
    if (!formData.senha) newErrors.senha = 'Senha é obrigatória';
    else if (formData.senha.length < 6) newErrors.senha = 'Senha deve ter no mínimo 6 caracteres';
    
    if (!formData.adminNome) newErrors.adminNome = 'Nome é obrigatório';
    if (!formData.adminCpf) newErrors.adminCpf = 'CPF é obrigatório';
    else if (!/^\d{11}$/.test(formData.adminCpf.replace(/\D/g, ''))) newErrors.adminCpf = 'CPF deve ter 11 dígitos';
    if (!formData.adminTelefone) newErrors.adminTelefone = 'Telefone é obrigatório';
    else if (formData.adminTelefone.replace(/\D/g, '').length < 10) newErrors.adminTelefone = 'Informe o telefone com DDD';
    if (!formData.adminDataNascimento) newErrors.adminDataNascimento = 'Data de nascimento é obrigatória';
    if (!formData.adminEndereco) newErrors.adminEndereco = 'Endereço é obrigatório';
    
    if (!formData.editoraNome) newErrors.editoraNome = 'Nome da editora é obrigatório';
    if (!formData.editoraRazaoSocial) newErrors.editoraRazaoSocial = 'Razão social é obrigatória';
    if (formData.editoraCnpj.replace(/\D/g, '').length !== 14) newErrors.editoraCnpj = 'CNPJ deve ter 14 dígitos';
    if (!formData.editoraDataAbertura) newErrors.editoraDataAbertura = 'Data de abertura é obrigatória';
    if (!formData.editoraEmail) newErrors.editoraEmail = 'E-mail da editora é obrigatório';
    if (formData.editoraTelefone.replace(/\D/g, '').length < 10) newErrors.editoraTelefone = 'Informe o telefone comercial com DDD';
    if (formData.editoraCep.replace(/\D/g, '').length !== 8) newErrors.editoraCep = 'CEP deve ter 8 dígitos';
    if (!formData.editoraEndereco) newErrors.editoraEndereco = 'Endereço comercial é obrigatório';
    
    if (!formData.termsAccepted) newErrors.termsAccepted = 'É preciso aceitar os Termos de uso para se cadastrar';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    try {
      const nameErr = await checkEditorName(formData.editoraNome.trim());
      if (nameErr) { setErrors((s) => ({ ...s, editoraNome: nameErr })); return; }
    } catch (_) { /* backend re-checks on submit */ }
    
    setLoading(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/trial-signup`, {
        email: formData.email,
        senha: formData.senha,
        adminNome: formData.adminNome,
        adminCpf: formData.adminCpf,
        adminDataNascimento: formData.adminDataNascimento,
        adminEndereco: formData.adminEndereco,
        adminTelefone: formData.adminTelefone,
        bibliotecaNome: formData.editoraNome,
        bibliotecaInstituicao: formData.editoraRazaoSocial,
        bibliotecaEndereco: formData.editoraEndereco,
        bibliotecaEmail: formData.editoraEmail,
        bibliotecaTelefone: formData.editoraTelefone,
        editoraCnpj: formData.editoraCnpj,
        editoraDataAbertura: formData.editoraDataAbertura,
        editoraCep: formData.editoraCep,
        editoraInscricaoEstadual: formData.editoraInscricaoEstadual,
        editoraSite: formData.editoraSite,
        editoraInstagram: formData.editoraInstagram,
        libraryType: 'digital',
        termsAccepted: true
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      navigate('/editor/dashboard');
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrors((s) => ({ ...s, submit: typeof d === 'string' ? d : 'Não foi possível concluir o cadastro. Confira os dados e tente novamente.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cadastro-page lv-public">
      <Header dark />
      
      <main className="cadastro-main">
        <div className="cadastro-container">
          <div className="cadastro-header">
            <h1 className="cadastro-title">
              Cadastre sua <span style={{ color: 'var(--lv-accent)' }}>Editora</span>
            </h1>
            <p className="cadastro-subtitle">
              Preencha os dados abaixo para começar a publicar seus livros e periódicos
            </p>
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem 1.5rem', 
              background: 'var(--lv-accent-soft)',
              borderRadius: '12px',
              border: '1px solid var(--lv-line)'
            }}>
              <p style={{ color: 'var(--lv-muted)', margin: 0, fontSize: '0.95rem' }}>
                <strong style={{ color: 'var(--lv-accent)' }}>Plano Editora:</strong> R$ 3.000/ano + 30% sobre cada assinatura vendida.
              </p>
              <p data-testid="cadastro-trial-note" style={{ color: 'var(--lv-accent-strong)', margin: '0.4rem 0 0', fontSize: '0.95rem', fontWeight: 600 }}>
                Teste grátis por 7 dias, sem cobranças. Contrate o plano pelo painel durante ou ao final do teste.
              </p>
              <p data-testid="cadastro-trial-effectivate-note" style={{ color: 'var(--lv-muted)', margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
                Seu catálogo começa com acesso gratuito. Para cobrar assinaturas dos leitores, defina o preço no menu Financeiro.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="cadastro-form">
            {/* Credenciais */}
            <Card className="form-section">
              <CardHeader>
                <CardTitle className="section-title-form">
                  <Lock className="w-5 h-5" />
                  Credenciais de Acesso
                </CardTitle>
              </CardHeader>
              <CardContent className="form-grid">
                <div className="form-field">
                  <Label htmlFor="email">E-mail de Login *</Label>
                  <div className="input-with-icon">
                    <Mail className="input-icon" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className={errors.email ? 'input-error' : ''}
                    />
                  </div>
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="form-field">
                  <Label htmlFor="senha">Senha *</Label>
                  <div className="input-with-icon">
                    <Lock className="input-icon" />
                    <Input
                      id="senha"
                      name="senha"
                      type="password"
                      value={formData.senha}
                      onChange={handleChange}
                      placeholder="Mínimo 6 caracteres"
                      className={errors.senha ? 'input-error' : ''}
                    />
                  </div>
                  {errors.senha && <span className="error-text">{errors.senha}</span>}
                </div>
              </CardContent>
            </Card>

            {/* Informações do Responsável */}
            <Card className="form-section">
              <CardHeader>
                <CardTitle className="section-title-form">
                  <User className="w-5 h-5" />
                  Responsável pela Editora
                </CardTitle>
              </CardHeader>
              <CardContent className="form-grid">
                <div className="form-field">
                  <Label htmlFor="adminNome">Nome Completo *</Label>
                  <div className="input-with-icon">
                    <User className="input-icon" />
                    <Input
                      id="adminNome"
                      name="adminNome"
                      value={formData.adminNome}
                      onChange={handleChange}
                      placeholder="Seu nome completo"
                      className={errors.adminNome ? 'input-error' : ''}
                    />
                  </div>
                  {errors.adminNome && <span className="error-text">{errors.adminNome}</span>}
                </div>

                <div className="form-field">
                  <Label htmlFor="adminCpf">CPF *</Label>
                  <div className="input-with-icon">
                    <CreditCard className="input-icon" />
                    <Input
                      id="adminCpf"
                      name="adminCpf"
                      value={formData.adminCpf}
                      onChange={handleChange}
                      placeholder="000.000.000-00"
                      className={errors.adminCpf ? 'input-error' : ''}
                    />
                  </div>
                  {errors.adminCpf && <span className="error-text">{errors.adminCpf}</span>}
                </div>

                <div className="form-field">
                  <Label htmlFor="adminTelefone">Telefone (Celular/WhatsApp) *</Label>
                  <div className="input-with-icon">
                    <Phone className="input-icon" />
                    <Input
                      id="adminTelefone"
                      name="adminTelefone"
                      value={formData.adminTelefone}
                      onChange={handleChange}
                      placeholder="(00) 00000-0000"
                      className={errors.adminTelefone ? 'input-error' : ''}
                    />
                  </div>
                  {errors.adminTelefone && <span className="error-text">{errors.adminTelefone}</span>}
                </div>

                <div className="form-field">
                  <Label htmlFor="adminDataNascimento">Data de Nascimento *</Label>
                  <div className="input-with-icon">
                    <Calendar className="input-icon" />
                    <Input
                      id="adminDataNascimento"
                      name="adminDataNascimento"
                      type="date"
                      value={formData.adminDataNascimento}
                      onChange={handleChange}
                      className={errors.adminDataNascimento ? 'input-error' : ''}
                    />
                  </div>
                  {errors.adminDataNascimento && <span className="error-text">{errors.adminDataNascimento}</span>}
                </div>

                <div className="form-field full-width">
                  <Label htmlFor="adminEndereco">Endereço *</Label>
                  <div className="input-with-icon">
                    <MapPin className="input-icon" />
                    <Input
                      id="adminEndereco"
                      name="adminEndereco"
                      value={formData.adminEndereco}
                      onChange={handleChange}
                      placeholder="Rua, número, bairro, cidade - UF"
                      className={errors.adminEndereco ? 'input-error' : ''}
                    />
                  </div>
                  {errors.adminEndereco && <span className="error-text">{errors.adminEndereco}</span>}
                </div>
              </CardContent>
            </Card>

            {/* Informações da Editora */}
            <Card className="form-section">
              <CardHeader>
                <CardTitle className="section-title-form">
                  <Building2 className="w-5 h-5" />
                  Dados da Editora
                </CardTitle>
              </CardHeader>
              <CardContent className="form-grid">
                <div className="form-field">
                  <Label htmlFor="editoraNome">Nome da Editora *</Label>
                  <div className="input-with-icon">
                    <Building2 className="input-icon" />
                    <Input
                      id="editoraNome"
                      name="editoraNome"
                      value={formData.editoraNome}
                      onChange={handleChange}
                      placeholder="Nome fantasia da editora"
                      className={errors.editoraNome ? 'input-error' : ''}
                    />
                  </div>
                  {errors.editoraNome ? <span className="error-text">{errors.editoraNome}</span> : <EditorNameHint nome={formData.editoraNome} />}
                </div>

                <div className="form-field">
                  <Label htmlFor="editoraRazaoSocial">Razão Social *</Label>
                  <div className="input-with-icon">
                    <Building2 className="input-icon" />
                    <Input
                      id="editoraRazaoSocial"
                      name="editoraRazaoSocial"
                      value={formData.editoraRazaoSocial}
                      onChange={handleChange}
                      placeholder="Razão social conforme o CNPJ"
                      className={errors.editoraRazaoSocial ? 'input-error' : ''}
                    />
                  </div>
                  {errors.editoraRazaoSocial && <span className="error-text">{errors.editoraRazaoSocial}</span>}
                </div>

                <CadField id="editoraCnpj" label="CNPJ *" icon={FileText} value={formData.editoraCnpj} onChange={handleChange} error={errors.editoraCnpj} placeholder="00.000.000/0000-00" inputMode="numeric" />
                <CadField id="editoraDataAbertura" label="Data de Abertura *" icon={Calendar} type="date" value={formData.editoraDataAbertura} onChange={handleChange} error={errors.editoraDataAbertura} />
                <CadField id="editoraInscricaoEstadual" label="Inscrição Estadual" icon={FileText} value={formData.editoraInscricaoEstadual} onChange={handleChange} placeholder="Se houver (ou Isento)" />

                <div className="form-field">
                  <Label htmlFor="editoraEmail">E-mail da Editora *</Label>
                  <div className="input-with-icon">
                    <Mail className="input-icon" />
                    <Input
                      id="editoraEmail"
                      name="editoraEmail"
                      type="email"
                      value={formData.editoraEmail}
                      onChange={handleChange}
                      placeholder="contato@editora.com"
                      className={errors.editoraEmail ? 'input-error' : ''}
                    />
                  </div>
                  {errors.editoraEmail && <span className="error-text">{errors.editoraEmail}</span>}
                </div>

                <CadField id="editoraTelefone" label="Telefone Comercial *" icon={Phone} value={formData.editoraTelefone} onChange={handleChange} error={errors.editoraTelefone} placeholder="(00) 0000-0000" inputMode="tel" />
                <CadField id="editoraCep" label="CEP *" icon={MapPin} value={formData.editoraCep} onChange={handleChange} error={errors.editoraCep} placeholder="00000-000" inputMode="numeric" />

                <div className="form-field full-width">
                  <Label htmlFor="editoraEndereco">Endereço Comercial *</Label>
                  <div className="input-with-icon">
                    <MapPin className="input-icon" />
                    <Input
                      id="editoraEndereco"
                      name="editoraEndereco"
                      value={formData.editoraEndereco}
                      onChange={handleChange}
                      placeholder="Rua, número, bairro, cidade - UF"
                      className={errors.editoraEndereco ? 'input-error' : ''}
                    />
                  </div>
                  {errors.editoraEndereco && <span className="error-text">{errors.editoraEndereco}</span>}
                </div>
                <CadField id="editoraSite" label="Site" icon={Globe} value={formData.editoraSite} onChange={handleChange} placeholder="https://www.suaeditora.com.br" />
                <CadField id="editoraInstagram" label="Instagram" icon={AtSign} value={formData.editoraInstagram} onChange={handleChange} placeholder="@suaeditora" />
              </CardContent>
            </Card>

            <TermsCheckbox checked={formData.termsAccepted} onChange={(v) => { setFormData({ ...formData, termsAccepted: v }); setErrors({ ...errors, termsAccepted: undefined }); }}
              testId="cadastro-terms-checkbox" error={errors.termsAccepted} />
            <div className="form-actions">
              <Button
                type="button"
                className="btn-secondary-custom"
                onClick={() => navigate('/')}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                data-testid="cadastro-submit-btn"
                className="btn-primary-custom"
                disabled={loading}
                style={{
                  background: 'var(--lv-ink)'
                }}
              >
                {loading ? 'Processando...' : 'Começar teste grátis de 7 dias'}
              </Button>
            </div>
            {errors.submit && <p data-testid="cadastro-submit-error" style={{ color: '#c53030', marginTop: '0.75rem', textAlign: 'center' }}>{errors.submit}</p>}
          </form>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CadastroPage;
